import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ExternalLink, Loader2, MessageCircleQuestion, Moon, Search, Sparkles, Sun, Tag } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useDarkMode } from '../contexts/DarkModeContext';
import {
  listPublicQuestionBrowser,
  type QuestionBrowserFacet,
  type QuestionBrowserItem,
  type QuestionBrowserResponse,
} from '../services/questionService';

const BrandLockup: React.FC<{ inverted?: boolean }> = ({ inverted = false }) => (
  <div className="flex items-center gap-4">
    <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-[1.35rem] shadow-brand ${inverted ? 'bg-white/10' : 'bg-brand-navy'}`}>
      <img src="/logos/combination_mark_yellow.svg" alt="Machine Learning Street Talk" className="h-8 w-auto" />
    </div>
    <div>
      <p className={`font-headlines text-2xl font-bold tracking-tight ${inverted ? 'text-brand-yellow' : 'brand-highlight-text'}`}>
        ReScript
      </p>
    </div>
  </div>
);

function mergeBrowserResponses(
  previous: QuestionBrowserResponse | null,
  next: QuestionBrowserResponse,
  append: boolean
): QuestionBrowserResponse {
  if (!append || !previous) {
    return next;
  }

  const deduped = new Map<string, QuestionBrowserItem>();
  [...previous.questions, ...next.questions].forEach((question) => {
    deduped.set(question.id, question);
  });

  return {
    ...next,
    questions: [...deduped.values()],
    count: [...deduped.values()].length,
  };
}

function formatTimestampParam(timestampSeconds: number | null): string {
  if (timestampSeconds === null || !Number.isFinite(timestampSeconds)) {
    return '';
  }

  const totalSeconds = Math.max(0, Math.floor(timestampSeconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h${minutes}m${seconds}s`;
  }

  return `${minutes}m${seconds}s`;
}

function groupQuestionsByTopic(questions: QuestionBrowserItem[]) {
  const groups = new Map<string, QuestionBrowserItem[]>();
  for (const question of questions) {
    const topic = (question.topic || 'Uncategorized').trim() || 'Uncategorized';
    const bucket = groups.get(topic);
    if (bucket) {
      bucket.push(question);
    } else {
      groups.set(topic, [question]);
    }
  }

  return [...groups.entries()]
    .map(([topic, items]) => ({ topic, items }))
    .sort((left, right) => {
      if (right.items.length !== left.items.length) {
        return right.items.length - left.items.length;
      }
      return left.topic.localeCompare(right.topic);
    });
}

const PublicQuestionExplorer: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [browserData, setBrowserData] = useState<QuestionBrowserResponse | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const lastRequestedOffsetRef = useRef(-1);
  const selectedTopic = searchParams.get('topic') || '';
  const selectedGuest = searchParams.get('guest') || searchParams.get('person') || '';
  const personFilter = selectedGuest || 'all';

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => window.clearTimeout(timeoutId);
  }, [query]);

  const updateSelectedTopic = useCallback((nextTopic: string, replace = false) => {
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      if (nextTopic) {
        nextParams.set('topic', nextTopic);
      } else {
        nextParams.delete('topic');
      }
      return nextParams;
    }, { replace });
  }, [setSearchParams]);

  const updateSelectedGuest = useCallback((nextGuest: string, replace = false) => {
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      nextParams.delete('person');
      if (nextGuest && nextGuest !== 'all') {
        nextParams.set('guest', nextGuest);
      } else {
        nextParams.delete('guest');
      }
      return nextParams;
    }, { replace });
  }, [setSearchParams]);

  const discoveryMode = debouncedQuery.length === 0 && !selectedTopic;

  const loadQuestions = useCallback(async ({ append = false, offset = 0 }: { append?: boolean; offset?: number } = {}) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await listPublicQuestionBrowser({
        query: debouncedQuery,
        person: personFilter === 'all' ? undefined : personFilter,
        topic: debouncedQuery ? undefined : (selectedTopic || undefined),
        discovery: discoveryMode,
        limit: discoveryMode ? 1 : 24,
        offset,
      });

      setBrowserData((previous) => mergeBrowserResponses(previous, response, append));
    } catch (err: any) {
      setError(err.message || 'Failed to load public questions');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedQuery, discoveryMode, personFilter, selectedTopic]);

  useEffect(() => {
    lastRequestedOffsetRef.current = -1;
    setBrowserData(null);
    void loadQuestions({ append: false, offset: 0 });
  }, [debouncedQuery, loadQuestions, personFilter, selectedTopic]);

  const people = useMemo(() => browserData?.people || [], [browserData]);
  const questions = useMemo(() => browserData?.questions || [], [browserData]);
  const topics = useMemo(() => browserData?.topics || [], [browserData]);
  const topicGroups = useMemo(() => groupQuestionsByTopic(questions), [questions]);
  const shellClassName = 'brand-shell brand-grid bg-[#F4F4F4] text-[#232E3F] dark:bg-[#101722] dark:text-[#F4F4F4]';
  const hasMore = Boolean(browserData?.pagination?.hasMore);
  const mutedTextClassName = isDarkMode ? 'text-[#B8C3D0]' : 'text-[#5B687C]';
  const headerCardClassName = isDarkMode
    ? 'border-white/[0.08] bg-[#162131]/88 text-[#F4F4F4]'
    : 'border-[#D6DCE4] bg-white/[0.88] text-[#232E3F]';
  const topicCardClassName = isDarkMode
    ? 'border-white/[0.08] bg-[#162131]/88 text-[#F4F4F4]'
    : 'border-[#D6DCE4] bg-white/90 text-[#232E3F]';
  const inputClassName = isDarkMode
    ? 'border-white/[0.12] bg-white/[0.08] text-[#F4F4F4]'
    : 'border-brand-navy/12 bg-white/90 text-brand-navy';

  useEffect(() => {
    if (debouncedQuery.trim().length > 0 && selectedTopic) {
      updateSelectedTopic('', true);
    }
  }, [debouncedQuery, selectedTopic, updateSelectedTopic]);

  useEffect(() => {
    if (discoveryMode || !hasMore || loading || loadingMore || !loadMoreRef.current) {
      return;
    }

    const node = loadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) {
          return;
        }

        const nextOffset = questions.length;
        if (lastRequestedOffsetRef.current === nextOffset) {
          return;
        }

        lastRequestedOffsetRef.current = nextOffset;
        void loadQuestions({ append: true, offset: nextOffset });
      },
      {
        rootMargin: '480px 0px',
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [discoveryMode, hasMore, loadQuestions, loading, loadingMore, questions.length]);

  const openPublicQuestion = (question: QuestionBrowserItem) => {
    const fallbackSuffix = question.timestampSeconds != null
      ? `?t=${formatTimestampParam(question.timestampSeconds)}`
      : '';
    window.location.assign(question.publicUrl || `/share/${question.shareToken || ''}${fallbackSuffix}`);
  };

  return (
    <div className={isDarkMode ? 'dark' : undefined}>
      <div className={shellClassName}>
        <div className="relative z-10">
        <header className="mx-auto max-w-7xl px-4 pt-5 sm:px-6 lg:px-8">
          <div className={`brand-card px-5 py-4 backdrop-blur-2xl sm:px-6 ${headerCardClassName}`}>
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <BrandLockup />
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={() => navigate('/')} className="brand-button-secondary">
                  <ArrowLeft className="h-4 w-4" />
                  Archive
                </button>
                <button
                  onClick={toggleDarkMode}
                  className="brand-button-secondary px-4"
                  title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  aria-pressed={isDarkMode}
                >
                  {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  <span className="hidden sm:inline">{isDarkMode ? 'Light' : 'Dark'}</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-7xl px-4 pb-16 pt-10 sm:px-6 lg:px-8 lg:pt-14">
          <section className="brand-card brand-card-muted px-6 py-8 sm:px-8">
            <p className="brand-kicker">
              <Sparkles className="h-3.5 w-3.5" />
              Question Browser
            </p>
            <h1 className="brand-highlight-text mt-6 max-w-4xl font-headlines text-5xl font-bold tracking-tight sm:text-6xl">
              Explore MLST through the questions that come up on the show.
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-brand-navy/72 dark:text-brand-cloud/72 sm:text-xl">
              Browse recurring MLST questions by topic and jump straight into the public transcript at the exact moment each one is discussed.
            </p>

            <div className="mt-8 grid gap-3 lg:grid-cols-[minmax(0,1.55fr)_260px_auto]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-navy/45 dark:text-brand-cloud/55" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search questions, answers, people, topics"
                  className={`w-full rounded-[1.35rem] border px-11 py-3 text-sm shadow-brand-sm outline-none transition focus:border-[#FFB52B]/50 ${inputClassName}`}
                />
              </div>
              <select
                value={personFilter}
                onChange={(event) => updateSelectedGuest(event.target.value)}
                className={`rounded-[1.35rem] border px-4 py-3 text-sm shadow-brand-sm outline-none transition focus:border-[#FFB52B]/50 ${inputClassName}`}
              >
                <option value="all">All people</option>
                {people.map((person: QuestionBrowserFacet) => (
                  <option key={person.name} value={person.name}>
                    {person.name} ({person.count})
                  </option>
                ))}
              </select>
              <button onClick={() => void loadQuestions({ append: false, offset: 0 })} className="brand-button-secondary">
                Refresh
              </button>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-brand-navy/54 dark:text-brand-cloud/60">
              {discoveryMode ? (
                <>
                  <span>{topics.length} topic{topics.length === 1 ? '' : 's'} ready to explore</span>
                  <span>Pick a topic or start typing to switch into search mode.</span>
                </>
              ) : (
                <>
                  {questions.length === 0 ? (
                    <span>No questions found for this search.</span>
                  ) : (
                    <>
                      <span>Showing {questions.length}{hasMore ? '+' : ''} question{questions.length === 1 ? '' : 's'}</span>
                      {hasMore ? <span>Scroll down to load more.</span> : null}
                    </>
                  )}
                </>
              )}
              {selectedTopic ? (
                <button
                  type="button"
                  onClick={() => updateSelectedTopic('')}
                  className="rounded-full border border-[#F2D9A6] bg-[#FFF4D9] px-3 py-1 font-headlines text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8A6100] shadow-[0_10px_22px_-18px_rgba(138,97,0,0.45)] dark:border-[#FFCC66]/70 dark:bg-[#FFCC66] dark:text-[#241600] dark:shadow-[0_16px_30px_-22px_rgba(0,0,0,0.55)]"
                >
                  Topic: {selectedTopic}
                </button>
              ) : null}
            </div>
          </section>

          <section className="mt-10 space-y-6">
            {loading ? (
              <div className="brand-card p-10 text-center">
                <Loader2 className="mx-auto h-8 w-8 animate-spin text-brand-yellow" />
                <p className="mt-4 text-sm leading-7 text-brand-navy/70 dark:text-[#F4F4F4]">Loading MLST questions…</p>
              </div>
            ) : error ? (
              <div className="brand-card p-10 text-center">
                <p className="brand-highlight-text font-headlines text-2xl font-bold tracking-tight">The public questions view did not load</p>
                <p className="mt-3 text-base leading-8 text-brand-navy/65 dark:text-[#C8CFD8]">{error}</p>
              </div>
            ) : discoveryMode ? (
              <div className="space-y-6">
                <div className="brand-card p-8 sm:p-10">
                  <p className="brand-highlight-text font-headlines text-2xl font-bold tracking-tight">Explore by topic</p>
                  <p className={`mt-3 max-w-3xl text-sm leading-7 ${mutedTextClassName}`}>
                    Start with a topic, then drill into the exact question and jump straight to the public transcript at that moment.
                  </p>
                </div>
                {topics.length === 0 ? (
                  <div className="brand-card p-10 text-center">
                    <Tag className="mx-auto h-10 w-10 text-brand-yellow" />
                    <p className="brand-highlight-text mt-6 font-headlines text-2xl font-bold tracking-tight">No topics are ready yet</p>
                    <p className={`mt-3 text-base leading-8 ${mutedTextClassName}`}>
                      Try a different person filter or start a search directly.
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {topics.map((topic) => (
                      <button
                        key={topic.name}
                        type="button"
                        onClick={() => updateSelectedTopic(topic.name || '')}
                        className={`rounded-[24px] border p-5 text-left shadow-brand-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-brand ${topicCardClassName}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="brand-highlight-text font-headlines text-xl font-bold tracking-tight">
                              {topic.name}
                            </p>
                            <p className={`mt-2 text-xs uppercase tracking-[0.18em] ${mutedTextClassName}`}>
                              {topic.count} question{topic.count === 1 ? '' : 's'}
                            </p>
                          </div>
                          <span className={`rounded-full px-3 py-1 font-headlines text-[11px] font-semibold uppercase tracking-[0.14em] ${isDarkMode ? 'border border-[#BCD7FF]/18 bg-[#213554]/88 text-[#F4F8FF]' : 'bg-[#EEF3FF] text-[#355E9A]'}`}>
                            Topic
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : topicGroups.length === 0 ? (
              <div className="brand-card p-10 text-center">
                <MessageCircleQuestion className="mx-auto h-10 w-10 text-brand-yellow" />
                <p className="brand-highlight-text mt-6 font-headlines text-2xl font-bold tracking-tight">No public questions matched</p>
                <p className="mt-3 text-base leading-8 text-brand-navy/65 dark:text-[#C8CFD8]">
                  Try a broader search or clear the person filter.
                </p>
              </div>
            ) : (
              <>
                {topicGroups.map((group) => (
                  <section key={group.topic} className="brand-card brand-card-muted overflow-hidden">
                    <div className="border-b border-brand-navy/10 px-6 py-4 dark:border-white/10">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="brand-highlight-text font-headlines text-xl font-bold tracking-tight">{group.topic}</p>
                          <p className="mt-1 text-xs uppercase tracking-[0.22em] text-brand-navy/50 dark:text-brand-cloud/56">
                            {group.items.length} question{group.items.length === 1 ? '' : 's'}
                          </p>
                        </div>
                        <div className={`rounded-full px-3 py-1 text-[11px] font-headlines font-semibold uppercase tracking-[0.16em] ${isDarkMode ? 'border border-[#BCD7FF]/18 bg-[#213554]/88 text-[#F4F8FF]' : 'bg-[#EEF3FF] text-[#355E9A]'}`}>
                          Topic group
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-4 p-5 xl:grid-cols-2">
                        {group.items.map((question) => (
                        <article
                          key={question.id}
                          className={`rounded-[24px] border p-5 shadow-brand-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-brand ${topicCardClassName}`}
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`rounded-full px-3 py-1 font-headlines text-[0.62rem] font-bold uppercase tracking-[0.22em] ${
                              question.questionType === 'implicit'
                                ? 'bg-[#EEF3FF] text-[#355E9A] dark:bg-[#26344A] dark:text-[#BCD7FF]'
                                : 'bg-[#EAF7EF] text-[#2D7250] dark:bg-[#153526] dark:text-[#A9E2C4]'
                            }`}>
                              {question.questionType}
                            </span>
                            {question.askedBy ? (
                              <span className={`rounded-full border px-3 py-1 font-headlines text-[0.62rem] font-bold uppercase tracking-[0.22em] ${isDarkMode ? 'border-white/12 bg-white/10 text-brand-cloud/76' : 'border-brand-navy/10 bg-white text-brand-navy/74'}`}>
                                {question.askedBy}
                              </span>
                            ) : null}
                          </div>

                          <h2 className="brand-highlight-text mt-5 font-headlines text-2xl font-bold tracking-tight">
                            {question.questionText}
                          </h2>
                          <p className="mt-2 text-sm uppercase tracking-[0.22em] text-brand-navy/48 dark:text-brand-cloud/56">
                            {question.sessionTitle}
                          </p>

                          <p className="mt-4 line-clamp-4 text-sm leading-7 text-brand-navy/68 dark:text-brand-cloud/74">
                            {question.answerSummary || 'Answer summary not yet written for this question.'}
                          </p>

                          <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-brand-navy/54 dark:text-brand-cloud/60">
                            {question.timecode ? <span>{question.timecode}</span> : <span>No timestamp</span>}
                            {question.publishedAt ? <span>{new Date(question.publishedAt).toLocaleDateString()}</span> : null}
                          </div>

                          <div className="mt-6 flex justify-end">
                            <button onClick={() => openPublicQuestion(question)} className="brand-button-primary" disabled={!question.shareToken && !question.publicUrl}>
                              Open public transcript
                              <ExternalLink className="h-4 w-4" />
                            </button>
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                ))}

                {hasMore ? (
                  <div ref={loadMoreRef} className="flex justify-center py-4">
                    <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm ${mutedTextClassName}`}>
                      <Loader2 className={`h-4 w-4 ${loadingMore ? 'animate-spin' : ''}`} />
                      Loading more questions as you scroll
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </section>
        </main>
        </div>
      </div>
    </div>
  );
};

export default PublicQuestionExplorer;
